/*
 * Dinowax product form — variant selection for the migrated Liquid sections.
 *
 * Replaces Replo's React variant handling. Progressive enhancement: the form is
 * a plain <form action="/cart/add"> with a hidden id already set to the default
 * variant, so add-to-cart works with this file absent. The theme's existing
 * Flex AJAX cart picks the submit up via its delegated `.ajax-submit` handler,
 * which is why the button keeps those class names.
 *
 * Markup contract:
 *   [data-dw-product-form]
 *     <script type="application/json" data-dw-variants>[...]</script>
 *     [data-dw-option="<option name>"] [data-dw-value="<value>"]   option buttons
 *     input[name="id"]                                             hidden, updated here
 *     [data-dw-price] / [data-dw-compare]                          updated here
 *     [data-dw-submit]                                             label + disabled state
 *     [data-dw-media="<variant id>"]                               optional, shown on match
 *     [data-dw-variant-image]                                         optional, src swapped
 */
(function () {
  'use strict';

  if (window.DWProductForm) { window.DWProductForm.init(); return; }

  function money(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var amount = parts.join('.');
    if (!format) return '$' + amount;
    /* Shopify money formats use {{amount}}-style placeholders. */
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function DWProductForm(root) {
    this.root = root;
    this.form = root.querySelector('form');
    this.idInput = root.querySelector('input[name="id"]');

    var data = root.querySelector('[data-dw-variants]');
    try {
      this.variants = data ? JSON.parse(data.textContent) : [];
    } catch (e) {
      this.variants = [];
    }
    if (!this.variants.length || !this.idInput) return;

    this.moneyFormat = root.getAttribute('data-dw-money-format') || '';
    this.soldOutText = root.getAttribute('data-dw-sold-out') || 'Sold out';
    this.addText = root.getAttribute('data-dw-add-text') || 'Add to cart';

    this.priceEl = root.querySelector('[data-dw-price]');
    this.compareEl = root.querySelector('[data-dw-compare]');
    this.submitEl = root.querySelector('[data-dw-submit]');
    this.buttons = Array.prototype.slice.call(root.querySelectorAll('[data-dw-option]'));

    this.buttons.forEach(function (btn) {
      btn.addEventListener('click', this.onSelect.bind(this, btn));
    }, this);

    var selects = root.querySelectorAll('select[data-dw-option-select]');
    Array.prototype.forEach.call(selects, function (sel) {
      sel.addEventListener('change', this.onSelectChange.bind(this, sel));
    }, this);

    this.variantImage = root.querySelector('[data-dw-variant-image]');
    if (this.variantImage) {
      this.baseImage = {
        url: this.variantImage.getAttribute('src'),
        alt: this.variantImage.getAttribute('alt')
      };
    }

    /* The first apply() sets up price and availability for whatever is already
       selected. It deliberately does NOT touch the card image: swapping it on
       load would repaint an art-directed grid the moment the page settled, for
       no shopper action. Only a real choice moves the picture. */
    this.apply(this.currentVariant() || this.variants[0]);
    this.ready = true;
    root.setAttribute('data-dw-product-form-ready', 'true');
  }

  /* Values currently chosen, indexed by option position. */
  DWProductForm.prototype.selection = function () {
    var chosen = {};
    this.buttons.forEach(function (btn) {
      if (btn.getAttribute('aria-pressed') === 'true' || btn.classList.contains('is-selected')) {
        chosen[btn.getAttribute('data-dw-option')] = btn.getAttribute('data-dw-value');
      }
    });
    var selects = this.root.querySelectorAll('select[data-dw-option-select]');
    Array.prototype.forEach.call(selects, function (sel) {
      chosen[sel.getAttribute('data-dw-option-select')] = sel.value;
    });
    return chosen;
  };

  DWProductForm.prototype.currentVariant = function () {
    var chosen = this.selection();
    var names = Object.keys(chosen);
    if (!names.length) return null;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var ok = true;
      for (var n = 0; n < names.length; n++) {
        if (String(v.options[names[n]]) !== String(chosen[names[n]])) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  DWProductForm.prototype.onSelect = function (btn, e) {
    if (e) e.preventDefault();
    var name = btn.getAttribute('data-dw-option');
    this.buttons.forEach(function (other) {
      if (other.getAttribute('data-dw-option') !== name) return;
      var on = other === btn;
      other.classList.toggle('is-selected', on);
      other.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    this.apply(this.currentVariant());
  };

  DWProductForm.prototype.onSelectChange = function () {
    this.apply(this.currentVariant());
  };

  DWProductForm.prototype.apply = function (variant) {
    if (!variant) {
      if (this.submitEl) {
        this.submitEl.disabled = true;
        this.submitEl.textContent = 'Unavailable';
      }
      return;
    }

    this.idInput.value = variant.id;

    if (this.priceEl) this.priceEl.textContent = money(variant.price, this.moneyFormat);

    if (this.compareEl) {
      var show = variant.compare_at_price && variant.compare_at_price > variant.price;
      this.compareEl.textContent = show ? money(variant.compare_at_price, this.moneyFormat) : '';
      this.compareEl.hidden = !show;
    }

    if (this.submitEl) {
      this.submitEl.disabled = !variant.available;
      this.submitEl.textContent = variant.available ? this.addText : this.soldOutText;
    }

    if (this.ready) this.paintCardImage(variant);

    var media = this.root.querySelectorAll('[data-dw-media]');
    if (media.length) {
      Array.prototype.forEach.call(media, function (el) {
        el.hidden = String(el.getAttribute('data-dw-media')) !== String(variant.id);
      });
    }

    this.root.dispatchEvent(new CustomEvent('dw:variant:change', {
      bubbles: true, detail: { variant: variant }
    }));
  };

  function normTitle(v) {
    return String(v == null ? '' : v).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /*
   * The photo a variant should show.
   *
   * Case packs carry no photo of their own — every "1 Gal (4 case)" and
   * "16 Oz (24 case)" in the catalogue has an empty image while the plain
   * "1 Gal" and "16 oz" beside them do not. Picking the longest other variant
   * title that this one starts with resolves them to the bottle they are a case
   * of, which is the rule as stated ("the 1 Gallon 4 case should show a 1
   * gallon") without a lookup table to keep in step with the products.
   *
   * Apparel, where no variant has a photo at all, matches nothing and falls
   * back to the product's own image, which is the right answer there too.
   */
  DWProductForm.prototype.imageFor = function (variant) {
    if (variant.image) return { url: variant.image, alt: variant.image_alt };

    var want = normTitle(variant.title);
    if (!want) return null;

    var best = null;
    var bestLen = 0;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (!v.image) continue;
      var cand = normTitle(v.title);
      // A shorter title the selection begins with: "1 gal" inside "1 gal (4 case)".
      if (!cand || cand.length >= want.length) continue;
      if (want.indexOf(cand) !== 0) continue;
      if (cand.length > bestLen) { best = v; bestLen = cand.length; }
    }
    return best ? { url: best.image, alt: best.image_alt } : null;
  };

  DWProductForm.prototype.paintCardImage = function (variant) {
    var img = this.variantImage;
    if (!img) return;

    var pick = this.imageFor(variant) || this.baseImage;
    if (!pick || !pick.url) return;
    if (img.getAttribute('src') === pick.url) return;

    img.setAttribute('src', pick.url);
    // srcset would otherwise keep serving the old photo at the size it matched.
    img.removeAttribute('srcset');
    if (pick.alt) img.setAttribute('alt', pick.alt);
  };

  DWProductForm.init = function (scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-dw-product-form]:not([data-dw-product-form-ready])');
    Array.prototype.forEach.call(nodes, function (el) {
      el.dwProductForm = new DWProductForm(el);
    });
  };

  window.DWProductForm = DWProductForm;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { DWProductForm.init(); });
  } else {
    DWProductForm.init();
  }

  document.addEventListener('shopify:section:load', function (e) { DWProductForm.init(e.target); });
})();
